import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, Users, Building2 } from 'lucide-react';
import ImageUploadField from '../../components/ImageUploadField';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Parceiros = () => {
  const { getAuthHeaders } = useAdmin();
  const { toast } = useToast();
  const [parceiros, setParceiros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingParceiro, setEditingParceiro] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    logo: '',
    tipo: 'restaurante',
    descricao: '',
    depoimento: '',
    responsavel: '',
    website: '',
    ordem: 0,
    ativo: true
  });

  useEffect(() => {
    loadParceiros();
  }, []);

  const loadParceiros = async () => {
    try {
      const response = await axios.get(`${API}/admin/parceiros`, {
        headers: getAuthHeaders()
      });
      setParceiros(response.data);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao carregar parceiros',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingParceiro) {
        await axios.put(
          `${API}/admin/parceiros/${editingParceiro.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast({ title: 'Parceiro atualizado com sucesso!' });
      } else {
        await axios.post(
          `${API}/admin/parceiros`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast({ title: 'Parceiro criado com sucesso!' });
      }
      
      setDialogOpen(false);
      resetForm();
      loadParceiros();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao salvar parceiro',
        variant: 'destructive'
      });
    }
  };

  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este parceiro?')) return;
    
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/parceiros/${id}`, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }
      });
      toast({ title: 'Parceiro deletado com sucesso!' });
      setParceiros(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao deletar parceiro',
        variant: 'destructive'
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (parceiro) => {
    setEditingParceiro(parceiro);
    setFormData({
      nome: parceiro.nome,
      logo: parceiro.logo,
      tipo: parceiro.tipo,
      descricao: parceiro.descricao || '',
      depoimento: parceiro.depoimento,
      responsavel: parceiro.responsavel,
      website: parceiro.website || '',
      ordem: parceiro.ordem || 0,
      ativo: parceiro.ativo !== false
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingParceiro(null);
    setFormData({
      nome: '',
      logo: '',
      tipo: 'restaurante',
      descricao: '',
      depoimento: '',
      responsavel: '',
      website: '',
      ordem: 0,
      ativo: true
    });
  };

  const getTipoLabel = (tipo) => {
    const tipos = {
      restaurante: 'Restaurante',
      cafe: 'Café',
      loja: 'Loja',
      evento: 'Evento',
      outro: 'Outro'
    };
    return tipos[tipo] || tipo;
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Parceiros</h1>
          <p className="text-white/60">Gerencie os parceiros da Santa Carne</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#cc8b3a] hover:bg-[#d4af37] text-black font-bold">
              <Plus className="w-4 h-4 mr-2" />
              Novo Parceiro
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gradient-to-br from-[#3a2f24] to-[#4a3f2e] border-[#cc8b3a]/20 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingParceiro ? 'Editar Parceiro' : 'Novo Parceiro'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                    className="bg-black border-[#cc8b3a]/30 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tipo">Tipo</Label>
                  <Select
                    value={formData.tipo}
                    onValueChange={(value) => setFormData({ ...formData, tipo: value })}
                  >
                    <SelectTrigger className="bg-black border-[#cc8b3a]/30 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gradient-to-br from-[#3a2f24] to-[#4a3f2e] border-[#cc8b3a]/30 text-white">
                      <SelectItem value="restaurante">Restaurante</SelectItem>
                      <SelectItem value="cafe">Café</SelectItem>
                      <SelectItem value="loja">Loja</SelectItem>
                      <SelectItem value="evento">Evento</SelectItem>
                      <SelectItem value="outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <ImageUploadField
                value={formData.logo}
                onChange={(url) => setFormData({ ...formData, logo: url })}
                getAuthHeaders={getAuthHeaders}
                label="Logo do Parceiro"
              />

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="responsavel">Responsável</Label>
                  <Input
                    id="responsavel"
                    value={formData.responsavel}
                    onChange={(e) => setFormData({ ...formData, responsavel: e.target.value })}
                    required
                    className="bg-black border-[#cc8b3a]/30 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">Website (opcional)</Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="bg-black border-[#cc8b3a]/30 text-white"
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="depoimento">Depoimento</Label>
                <Textarea
                  id="depoimento"
                  value={formData.depoimento}
                  onChange={(e) => setFormData({ ...formData, depoimento: e.target.value })}
                  required
                  rows={3}
                  className="bg-black border-[#cc8b3a]/30 text-white"
                  placeholder="O que o parceiro diz sobre a Santa Carne..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição (opcional)</Label>
                <Textarea
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  rows={2}
                  className="bg-black border-[#cc8b3a]/30 text-white"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="ativo"
                  checked={formData.ativo}
                  onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="ativo" className="cursor-pointer">
                  Parceiro Ativo
                </Label>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-[#cc8b3a]/30 text-white"
                >
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#cc8b3a] hover:bg-[#d4af37] text-black">
                  {editingParceiro ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Parceiros Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#cc8b3a]"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {parceiros.map((parceiro) => (
            <Card key={parceiro.id} className="bg-gradient-to-br from-[#3a2f24] to-[#4a3f2e] border-[#cc8b3a]/20">
              <CardContent className="p-4">
                <div className="bg-white/10 rounded-lg p-4 mb-4 flex items-center justify-center h-32">
                  <img
                    src={parceiro.logo}
                    alt={parceiro.nome}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <h3 className="text-lg font-bold text-white mb-1">{parceiro.nome}</h3>
                <p className="text-sm text-[#cc8b3a] mb-2 flex items-center">
                  <Building2 className="w-3 h-3 mr-1" />
                  {getTipoLabel(parceiro.tipo)}
                </p>
                <p className="text-sm text-white/60 mb-2">
                  Responsável: {parceiro.responsavel}
                </p>
                <p className="text-sm text-white/50 mb-4 line-clamp-2 italic">
                  "{parceiro.depoimento}"
                </p>
                <div className="flex space-x-2">
                  <Button
                    size="sm"
                    onClick={() => handleEdit(parceiro)}
                    className="flex-1 bg-[#cc8b3a] hover:bg-[#d4af37] text-black"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(parceiro.id)}
                    disabled={deletingId === parceiro.id}
                    data-testid={`delete-parceiro-${parceiro.id}`}
                    className="border-red-500/30 text-red-500 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!loading && parceiros.length === 0 && (
        <div className="text-center py-20">
          <Users className="w-16 h-16 mx-auto text-white/30 mb-4" />
          <p className="text-white/60 text-lg">Nenhum parceiro cadastrado</p>
          <p className="text-white/40 text-sm">Clique em "Novo Parceiro" para começar</p>
        </div>
      )}
    </div>
  );
};

export default Parceiros;
